import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { JobDto } from '../../../dtos';
import { emptyGuid, hasChanged } from '../../../util';
import { IFormFieldValidationConfig, IFormProps, runFieldValidation, runFormValidation } from '../../CoreLib/library';

export interface IJobFormValues {
    isActive: boolean;
}

export const DEFAULT_JOB: JobDto = {
    id: emptyGuid,
    clientId: emptyGuid,
    quotes: [],
    isActive: true,
    createdOn: new Date(),
};

export function useJobForm(props: IFormProps<JobDto>) {
    const { save, cancel, initValues } = props;
    const [formQuoteId, setFormQuoteId] = useState('');
    const [formIsActive, setFormIsActive] = useState(true);

    useEffect(() => {
        setFormQuoteId(initValues?.currentQuote?.id ?? '');
        setFormIsActive(initValues?.isActive ?? true);
    }, [initValues]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof IJobFormValues, string>>(new Map([]));

    const formFieldValidators = useMemo(() => new Map<keyof IJobFormValues, IFormFieldValidationConfig>([]), []);

    const getCurrentFormValues = useCallback((): IJobFormValues => {
        return {
            isActive: formIsActive,
        };
    }, [formIsActive]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<IJobFormValues>>(formValues, formFieldValidators);
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const validateField = useCallback(
        (fieldName: keyof IJobFormValues) => {
            const validationConfig = formFieldValidators.get(fieldName);
            if (validationConfig) {
                const formValues = getCurrentFormValues();
                const fieldValue = formValues[fieldName];
                const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

                if (errorMessage !== fieldErrors.get(fieldName)) {
                    const updatedFieldErrors = _.cloneDeep(fieldErrors);
                    updatedFieldErrors.set(fieldName, errorMessage);
                    setFieldErrors(updatedFieldErrors);
                }
            }
        },
        [fieldErrors, formFieldValidators, getCurrentFormValues]
    );

    const isFormDirty = useCallback(() => {
        var isDirty = hasChanged(formIsActive, initValues?.isActive);

        return isDirty;
    }, [formIsActive, initValues?.isActive]);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
    }, []);

    const handleSave = useCallback(() => {
        const isFormValid = validateForm();
        if (isFormValid) {
            const formValues = getCurrentFormValues();
            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_JOB),
                ...formValues,
            };
            save(updatedUserRecord);
        }
    }, [getCurrentFormValues, initValues, save, validateForm]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleIsActiveChange,
        formQuoteId,
        formIsActive,
        validateField,
    };
}
